import { computed, reactive } from 'vue'
import client from '@/lib/API/client'
import { readNotificationsCopy, writeNotificationsCopy } from '@/lib/sqlite/features/notifications-feature'
import { appendJournal, copyRules, markRead, mergeJournal, offlineCopy, unreadOf } from '@/lib/notifications/journal'
import { forgetPhoneConfig, loadPhoneConfig } from '@/lib/notifications/phone-config'

// Subject 7: the notifications journal of the signed-in person (GET /notifications),
// one state for the bell and the journal page. The phone keeps a copy of it as the
// admin set it (by default the 100 newest of the 60 last days, or none at all),
// read without network and purged at logout.
const COPY_KEY = 'journal'

const state = reactive({ items: null, nextCursor: null, unread: null, failed: false, loading: false })

let copyRead = null
// Whether the state holds what the copy held (read, none on the phone, or purged
// at logout): until then, a write would replace rows the state does not have.
let copyHeld = false
// Bumped at logout: whatever was in flight then (a read, a page, a tap) keeps nothing.
let generation = 0
const signedOut = (since) => since !== generation

const eraseCopy = () => writeNotificationsCopy(COPY_KEY, null)

// Read after rows of the API (its first read failed), the copy only adds what it
// holds past them, as a refresh would; nothing when they are the whole journal.
const withCopy = (copy) =>
    state.items === null ? copy : mergeJournal(copy, state.items, { complete: state.nextCursor === null })

/**
 * The copy into the state, once. A read that fails (the 15 s timeout of a cold
 * start, a push tapped first) is tried again by the next caller. Turned off by
 * the admin: what the phone held leaves it.
 */
const readCopy = () => {
    const reading = generation
    copyRead ??= loadPhoneConfig()
        .then(async (config) => {
            const rules = copyRules(config)
            if (!rules.enabled) await eraseCopy()
            const copy = rules.enabled ? await readNotificationsCopy(COPY_KEY) : null
            if (signedOut(reading)) return
            if (Array.isArray(copy)) state.items = withCopy(offlineCopy(copy, rules))
            copyHeld = true
        })
        .catch(() => {
            if (!signedOut(reading)) copyRead = null
        })
    return copyRead
}

// Never over a copy the state does not hold, and none when the admin turned it off.
const keepCopy = async (since) => {
    const rules = copyRules(await loadPhoneConfig())
    if (signedOut(since)) return
    if (!rules.enabled) return eraseCopy().catch(() => undefined)
    if (copyHeld && state.items !== null) {
        await writeNotificationsCopy(COPY_KEY, offlineCopy(state.items, rules)).catch(() => undefined)
    }
}

/** Reads the first page again over the copy. Offline, the copy stays. Never throws. */
export async function refreshJournal() {
    await readCopy()
    const since = generation
    state.loading = true
    try {
        const { data } = await client.get('/notifications')
        if (signedOut(since)) return
        state.items = mergeJournal(state.items, data.data, { complete: !data.next_cursor })
        state.nextCursor = data.next_cursor ?? null
        state.unread = data.unread_count ?? null
        state.failed = false
        await keepCopy(since)
    } catch {
        if (!signedOut(since)) state.failed = state.items === null
    } finally {
        if (!signedOut(since)) state.loading = false
    }
}

/** The next page, after the rows shown. */
export async function loadMoreJournal() {
    if (!state.nextCursor || state.loading) return
    const since = generation
    state.loading = true
    try {
        const { data } = await client.get('/notifications', { params: { cursor: state.nextCursor } })
        if (signedOut(since)) return
        state.items = appendJournal(state.items, data.data)
        state.nextCursor = data.next_cursor ?? null
        state.unread = data.unread_count ?? state.unread
        await keepCopy(since)
    } finally {
        if (!signedOut(since)) state.loading = false
    }
}

/** Every row read (POST /notifications/read { all }); rejects offline. */
export async function markAllRead() {
    const since = generation
    const { data } = await client.post('/notifications/read', { all: true })
    if (signedOut(since)) return
    state.items = markRead(state.items)
    state.unread = data?.unread_count ?? 0
    await keepCopy(since)
}

/**
 * Tapped, in the journal or on the push itself (its notification_id): read, and
 * opened in the history. Best effort: offline, the row is read on this phone only.
 */
export async function markOpened(id) {
    if (!id) return
    await readCopy()
    const since = generation
    const wasUnread = state.items?.some((item) => item.id === id && !item.read_at)
    if (state.items) state.items = markRead(state.items, [id])
    if (wasUnread && state.unread) state.unread -= 1
    try {
        const { data } = await client.post(`/notifications/${id}/opened`)
        if (signedOut(since)) return
        state.unread = data?.unread_count ?? state.unread
    } catch {
        // offline or gone: nothing more to do
    }
    await keepCopy(since)
}

/** Signed out: nothing of the account stays in memory (the logout purges the copy). */
export function forgetNotifications() {
    generation += 1
    Object.assign(state, { items: null, nextCursor: null, unread: null, failed: false, loading: false })
    copyRead = Promise.resolve()
    // The logout purges the copy: nothing left on the phone for the next account's rows to replace.
    copyHeld = true
    forgetPhoneConfig()
}

export default () => ({
    items: computed(() => state.items),
    // The server's count once read, the copy's offline.
    unread: computed(() => state.unread ?? unreadOf(state.items)),
    hasMore: computed(() => !!state.nextCursor),
    loading: computed(() => state.loading),
    failed: computed(() => state.failed),
})
